import { useEffect, useRef } from "react";
import { toArabicNumber } from "@/data/quranMeta";

/**
 * Surah-mode reading view: full-page Mushaf-style layout with
 * a decorative banner header, optional Bismillah row, and the
 * justified Arabic body where each ayah is wrapped in a span
 * that handles tap/long-press/drag/contextmenu interactions.
 */
export default function MushafView({
  surah,
  surahNumber,
  showTransliteration,
  displayAyahs,
  stackMode,
  stackLevel,
  stackCurrentIndex,
  currentAyahIndex,
  isPlaying,
  readingPreset,
  fontPreset,
  handleDragStart,
  addBookmark,
  isMemorized,
  handleSurahAyahTap,
  startSurahAyahPress,
  cancelSurahAyahPress,
  moveSurahAyahPress,
  selectedKeys,
}) {
  const surahNum = parseInt(surahNumber);
  // Refs keyed by ayah index → DOM span, so we can scroll the active one
  // into view when the audio progresses to it.
  const ayahRefs = useRef({});
  useEffect(() => {
    if (!isPlaying) return;
    const el = ayahRefs.current[currentAyahIndex];
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [currentAyahIndex, isPlaying]);

  return (
    <div
      data-testid="mushaf-view"
      className="mushaf-page mushaf-parchment rounded-2xl shadow-xl overflow-hidden border border-[#E8D5A3]"
    >
      <div className="mushaf-banner py-6 px-8">
        <div className="flex items-center justify-center">
          <div className="relative">
            <div className="absolute -left-16 top-1/2 -translate-y-1/2 w-12 h-[2px] bg-gradient-to-r from-transparent to-[#C9A227]"></div>
            <div className="absolute -right-16 top-1/2 -translate-y-1/2 w-12 h-[2px] bg-gradient-to-l from-transparent to-[#C9A227]"></div>
            <div className="border-2 border-[#C9A227]/60 rounded-xl px-10 py-3 bg-[#0D2818]/50">
              <p className="font-amiri text-3xl text-[#E8D5A3] text-center">{surah?.name}</p>
            </div>
          </div>
        </div>
        <p className="text-center text-[#8B9D83] font-outfit text-sm mt-3">
          {surah?.englishName} • {surah?.englishNameTranslation}
        </p>
      </div>

      {surahNum !== 9 && (
        <div className="py-4 border-b border-[#E8D5A3]/30 text-center">
          <p className="font-amiri text-2xl mushaf-bismillah">﷽</p>
          {showTransliteration && (
            <p className="font-outfit text-xs mushaf-bismillah-caption mt-2 italic">
              Bismillah ir-Rahman ir-Raheem
            </p>
          )}
        </div>
      )}

      <div className="px-6 sm:px-10 py-14">
        <div
          className="quran-text font-noto-naskh mushaf-arabic"
          style={{
            direction: "rtl",
            textAlign: "justify",
            textAlignLast: "center",
            lineHeight: String(readingPreset.lineHeight),
            fontSize: `${fontPreset.surahPx}px`,
          }}
        >
          {displayAyahs.map((ayah, idx) => {
            const isInCurrentStack = stackMode && idx < stackLevel;
            const isCurrentlyPlaying = currentAyahIndex === idx && isPlaying;
            const isInStackRange =
              stackMode && isPlaying && idx <= stackCurrentIndex && idx < stackLevel;
            const key = `${surahNum}:${ayah.numberInSurah}`;
            const isSelected = selectedKeys?.has?.(key);

            return (
              <span
                ref={(el) => {
                  if (el) ayahRefs.current[idx] = el;
                  else delete ayahRefs.current[idx];
                }}
                key={ayah.numberInSurah}
                data-testid={`surah-ayah-${ayah.numberInSurah}`}
                draggable
                onDragStart={(e) => handleDragStart(e, ayah, surahNum)}
                onContextMenu={(e) => e.preventDefault()}
                onPointerDown={(e) => startSurahAyahPress?.(ayah, e)}
                onPointerMove={(e) => moveSurahAyahPress?.(e)}
                onPointerUp={cancelSurahAyahPress}
                onPointerLeave={cancelSurahAyahPress}
                onPointerCancel={cancelSurahAyahPress}
                title="Long-press to open actions"
                className={`inline cursor-pointer transition-all duration-300 rounded-sm
                  ${isCurrentlyPlaying ? "bg-[#C9A227]/40 px-2 py-1 shadow-lg ring-2 ring-[#C9A227]/60" : ""}
                  ${!isCurrentlyPlaying && isSelected ? "bg-[#C9A227]/25 ring-1 ring-[#C9A227]/50 px-1" : ""}
                  ${isInStackRange && !isCurrentlyPlaying && !isSelected ? "bg-[#C9A227]/15 px-1" : ""}
                  ${!isCurrentlyPlaying && !isInStackRange && !isSelected ? "hover:bg-[#C9A227]/10" : ""}
                  ${isMemorized(surahNumber, ayah.numberInSurah) ? "text-[#1B4D3E]" : ""}`}
                onClick={() => handleSurahAyahTap(ayah)}
              >
                {ayah.text}
                <span
                  className={`inline-flex items-center justify-center w-7 h-7 mx-1 align-middle font-bold transition-all select-none ${
                    isCurrentlyPlaying
                      ? "scale-110"
                      : isInCurrentStack
                      ? "ring-1 ring-[#C9A227]/50"
                      : ""
                  }`}
                  style={{
                    fontFamily: "'Amiri', serif",
                    fontSize: "0.85rem",
                    background: isCurrentlyPlaying
                      ? "radial-gradient(circle, #E8C75B 0%, #C9A227 80%)"
                      : "radial-gradient(circle, #FBF6E9 0%, #F5E9C3 70%, #E8D5A3 100%)",
                    border: `1.5px solid ${isCurrentlyPlaying ? "#0D2818" : "#C9A227"}`,
                    borderRadius: "50%",
                    color: isCurrentlyPlaying ? "#0D2818" : "#1B4D3E",
                    boxShadow: isCurrentlyPlaying
                      ? "0 0 18px rgba(232,199,91,0.65), inset 0 0 0 1px #FBF6E9"
                      : "inset 0 0 0 1px #C9A227, inset 0 1px 2px rgba(255,255,255,0.6), 0 1px 2px rgba(0,0,0,0.08)",
                  }}
                >
                  {toArabicNumber(ayah.numberInSurah)}
                </span>
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
